import {inject, isRef, ref, Ref, unref, watch} from "vue";
import {StateChecker, Transform} from "/@src/components/custom/form/BasePanelForm.vue";
import {
    injectKeyFormConfig,
    injectKeyFormData,
    injectKeyFormFieldLayout,
    injectKeyUpdateFormData, injectRegisterFormResetHooks
} from "/@src/constants/keys";
import {getNestedField} from "/@src/utils";
import {MaybeRef} from "@vueuse/shared";

export interface FormPanelOptions<T> {
    defaultValue?:T
    transform?:Transform<any,T>
    index?:number
    deepWatch?:boolean
    immediate?:boolean
    ignoreFieldUndefined?:boolean
    // 临时用，用于是否只watch自己的field
    only?:boolean
    dependency?:Array<string>
    /**
     * 处理formData的函数，返回false表示不自动处理后续
     * @param formData
     */
    dataHandler?: (formData:any) => boolean
    dataWatcher?: (newValue:T) => void
    onReset?: () => void
    addonModel?:Ref<any>
    checker?:StateChecker<T>
    manualUpdate?:boolean//是否手动更新
}
export const useFormField = <T,Form=void>(model:Ref<T|undefined>, fieldMaybeRef?:Ref<string|undefined>|string|undefined, options?:FormPanelOptions<T>) => {
    const formData = inject<Ref<Form>>(injectKeyFormData)!
    const updateForm = inject(injectKeyUpdateFormData)!
    const config = inject(injectKeyFormConfig)!
    const defaultLayout = inject(injectKeyFormFieldLayout)
    const visible = ref(true)
    const disabled = ref(false)
    let field = unref(fieldMaybeRef);

    if (options?.onReset){
        const registerReset = inject(injectRegisterFormResetHooks)
        registerReset?.(options.onReset)
    }

    const setupLocalModel = (newVal:any) => {
        if (Array.isArray(newVal)) {
            model.value = field?getNestedField(newVal, field,options?.index??0,options?.ignoreFieldUndefined):newVal[options?.index??0]
        } else {
            if (field){
                model.value = getNestedField(newVal, field,-1,options?.ignoreFieldUndefined)
            }
        }
        if (model.value === undefined && options?.defaultValue !== undefined && options?.defaultValue !== null) {
            model.value = options?.defaultValue
        }
    }
    if (options?.only){
        watch(()=>{
            if (Array.isArray(formData.value)){
                if (field){
                    return getNestedField(formData.value, field,options?.index??-1, options?.ignoreFieldUndefined)
                } else {
                    return formData.value[options.index??0]
                }

            } else {
                if (field){
                    return getNestedField(formData.value, field, options?.index??-1,options?.ignoreFieldUndefined)
                } else {
                    return formData.value
                }
            }
        }, (newVal, old) => {
            if (options?.transform?.get) {
                // 使用transform时必须要有field
                model.value = options?.transform?.get(formData.value,field!,model.value,options.index)??options?.defaultValue
            } else {
                model.value = newVal
            }
            // 由于表单可能复杂嵌套，可能有性能问题
        }, {deep: options?.deepWatch,immediate: true})
    } else {
        /**
         * 在表单下时，不需要更新vmodel
         */
        watch(formData!, (newVal,old) => {
            updateFromForm(newVal)
            // 由于表单可能复杂嵌套，可能有性能问题
        }, {deep: options?.deepWatch,immediate: true})
    }

    // 支持动态field
    if (field&&isRef(fieldMaybeRef)){
        watch(fieldMaybeRef, (newVal,old) => {
            field = newVal
            updateFromForm(formData?.value)
        })
    }

    if (!options?.manualUpdate) {
        watch(model, (newVal) => {
            update(newVal)
            options?.dataWatcher?.(newVal)
        })
    }

    if (options?.addonModel) {
        watch(options.addonModel, (newVal) => {
            update(model.value)
        })
    }

    const update = (value?:T) => {
        model.value = value
        if (options?.transform) {
            // 使用transform时必须要有field
            options.transform.set(formData?.value,field!,value!,options?.index)
        } else {
            // 待观察
            // if(!Array.isArray(value) && typeof value !== 'object'){
            //     updateForm?.(field??null, value,options?.index)
            // }
            // if (!Array.isArray(formData?.value)){
                updateForm?.(field??null, value,options?.index)
            // }
        }
    }

    function updateFromForm(newVal:any) {
        if (options?.dataHandler?.(newVal) === false) {
            return
        }
        if (newVal) {
            // 数组时可能有问题
            if (options?.transform?.get) {
                // 使用transform时必须要有field
                model.value = options?.transform?.get(newVal,field!,model.value,options.index)??options?.defaultValue
            } else {
                setupLocalModel(newVal)
            }
        } else {
            if (options?.defaultValue !== undefined && options?.defaultValue !== null) {
                model.value = options?.defaultValue
            }
        }
        if (options?.checker?.isVisible) {
            visible.value = options?.checker?.isVisible(formData?.value,field!,model.value!,options?.index)
        }
        if (options?.checker?.isDisabled) {
            disabled.value = options?.checker?.isDisabled(formData?.value,field!,model.value!,options?.index)
        }
    }

    function refresh() {
        update(model.value)
    }

    return {
        update,
        config,
        formData,
        updateForm,
        defaultLayout,
        visible,
        disabled
    }
}
